import { useEffect, useRef, useState } from 'react';
import { url } from '../../../../api';
import { useImageCache } from '../../../../context/ImageCacheContext';
import { coffeeRatingPaths } from '../../api/paths';

interface UseCardImageResult {
    cardRef: React.RefObject<HTMLDivElement>;
    imageUrl: string | null;
    imageLoaded: boolean;
    fetchFailed: boolean;
    isVisible: boolean;
    onImageLoad: () => void;
    onImageError: () => void;
}

export const useCardImage = (ratingId: string): UseCardImageResult => {
    const { getImage, setImage, isLoading, setLoading } = useImageCache();
    const cardRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [fetchFailed, setFetchFailed] = useState(false);

    // intersection observer: only fetch once the card is near the viewport
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1, rootMargin: '500px 0px' }
        );
        if (cardRef.current) observer.observe(cardRef.current);
        return () => observer.disconnect();
    }, []);

    // fetch the image URL once visible (and not already cached / in-flight / failed).
    // Don't cancel on re-runs — `setLoading(true)` itself triggers a re-render which
    // would otherwise cleanup the in-flight fetch and lose the URL we just retrieved.
    useEffect(() => {
        if (!isVisible || !ratingId || fetchFailed) return;
        if (getImage(ratingId)) {
            setImageLoaded(true);
            return;
        }
        if (isLoading(ratingId)) return;

        const fetchImage = async () => {
            try {
                setLoading(ratingId, true);
                const response = await fetch(`${url}${coffeeRatingPaths.image(ratingId)}`);
                if (!response.ok) {
                    console.error('Failed to fetch image URL');
                    setFetchFailed(true);
                    return;
                }
                const data = await response.json();
                if (!data.imageUrl) {
                    console.error('No image URL in response');
                    setFetchFailed(true);
                    return;
                }
                setImage(ratingId, data.imageUrl);
            } catch (error) {
                console.error('Error fetching coffee image:', error);
                setFetchFailed(true);
            } finally {
                setLoading(ratingId, false);
            }
        };

        fetchImage();
    }, [ratingId, isVisible, fetchFailed, getImage, setImage, isLoading, setLoading]);

    return {
        cardRef,
        imageUrl: getImage(ratingId) || null,
        imageLoaded,
        fetchFailed,
        isVisible,
        onImageLoad: () => setImageLoaded(true),
        onImageError: () => setFetchFailed(true),
    };
};
